<template>
  <div
    :class="cn(cardVariants({ type, mobile: maxSmall, overdue }), props.class)"
    @click.stop="clickTask"
  >
    <div v-if="isSelectable && desktopList" class="flex-none ltr:mr-4 rtl:ml-4">
      <Checkbox
        :checked="checked"
        @update:checked="$emit('select')"
        size="sm"
      />
    </div>
    <div class="w-full flex flex-col">
      <div
        class="flex items-center gap-x-2 mb-1"
        :class="{
          'justify-start': mobileOrKanban,
          'justify-between': desktopList,
        }"
      >
        <Breadcrumb
          :items="breadcrumb"
          :limit="maxSmall ? 0 : 4"
          :isScrollable="maxSmall"
          :size="maxSmall ? 'md' : 'sm'"
        />
        <DueDate
          v-if="desktopList"
          :dueDate="task.completed_at || task.scheduled_for"
          :isOverdue="overdue"
          :size="maxSmall ? 'md' : 'sm'"
          class="whitespace-nowrap"
        />
      </div>
      <div
        class="flex w-full grow"
        :class="{
          'flex-col ': mobileOrKanban,
          'flex-row justify-between gap-3 items-end': desktopList,
        }"
      >
        <component
          class="text-secondary text-style-base font-medium flex"
          :is="showDescriptionTooltip ? Tooltip : 'span'"
          :title="task.description"
          triggerAs="span"
          side="bottom"
          :class="{
            'mb-3 grow !line-clamp-2': mobileOrKanban,
            truncate: !mobileOrKanban,
          }"
        >
          <div
            ref="descriptionContainer"
            :class="{ 'truncate max-w-full': !mobileOrKanban }"
          >
            {{ task.description }}
          </div>
        </component>
        <div
          class="flex gap-x-1 gap-y-2 items-center shink-0"
          :class="{
            'grow max-w-full': mobileOrKanban,
          }"
        >
          <Recurring v-if="!!task.recurrence" :size="maxSmall ? 'md' : 'sm'" />
          <Visibility
            :isPrivate="!!task.problem?.is_private"
            :iconOnly="true"
            :size="maxSmall ? 'md' : 'sm'"
          />
          <Divider orientation="vertical" class="h-5.5" />
          <Priority
            :priority="task.priority?.id"
            :iconOnly="mobileOrKanban"
            :variant="maxSmall ? 'dark' : 'light'"
            :size="maxSmall ? 'md' : 'sm'"
          />
          <ChecklistBadge
            :total="task.checklist_items"
            :checked="task.checked_checklist_items"
            :size="maxSmall ? 'md' : 'sm'"
            showIcon
          />
          <Tooltip
            v-if="task.category"
            :title="task.category?.name"
            side="bottom"
            class="!w-fit"
            disableHoverableContent
            ignorenonkeyboardfocus
          >
            <Badge
              :label="task.category?.name"
              type="light"
              color="indigo"
              :size="maxSmall ? 'md' : 'sm'"
              :class="mobileOrKanban ? 'max-w-[120px]' : 'max-w-[180px]'"
            />
          </Tooltip>
          <Divider orientation="vertical" class="h-5.5" />
          <Assignees
            :assignees="task.assignees"
            :size="maxSmall ? 'md' : 'sm'"
            :show-names="!mobileOrKanban"
          />
          <DueDate
            v-if="mobileOrKanban"
            :dueDate="task.completed_at || task.scheduled_for"
            :isOverdue="overdue"
            :size="maxSmall ? 'md' : 'sm'"
            class="ltr:ml-auto rtl:mr-auto"
          />
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { computed, ref, watch, onMounted } from "vue";
import { Checkbox } from "@/SharedComponents/Checkboxes";
import {
  Visibility,
  Priority,
  Badge,
  Recurring,
  ChecklistBadge,
} from "@/SharedComponents/Badges";
import { DueDate } from "@/SharedComponents/Dates";
import { Breadcrumb } from "@/SharedComponents/Breadcrumbs";
import { Assignees } from "@/SharedComponents/Assignees";
import { Divider } from "@/SharedComponents/Divider";
import { Tooltip } from "@/SharedComponents/Tooltips";
import { useBreakpoints } from "@/Composables/useBreakpoints";
import { useTask } from "@/Composables/useTask";
import { types, cardVariants } from ".";
import { cn } from "@/Utils/cn";

const emit = defineEmits(["click", "select"]);

const props = defineProps({
  task: { type: Object, required: true },
  type: {
    type: String,
    validator: (value) => types.includes(value),
    default: "list",
  },
  checked: { type: Boolean, required: false },
  isSelectable: { type: Boolean, required: false },
  class: { type: null, required: false },
});

const descriptionContainer = ref();
const showDescriptionTooltip = ref(false);

const { maxSmall, width } = useBreakpoints();
const { getBreadcrumb } = useTask(props.task);

const breadcrumb = computed(() => {
  return getBreadcrumb(props.task);
});

// temporary id for the task, will be replaced by the actual id once developed
// @todo: replace with actual id
// const id = computed(() => props.task.id.split("-").pop());

// is overdue if the task is scheduled and the current date is after the scheduled date
const overdue = computed(() => {
  if (!props.task.scheduled_for) return false;
  if (props.task.completed_at) return false;

  return Date.now() > new Date(props.task.scheduled_for);
});

// shorthands for main layout styles
const mobileOrKanban = computed(
  () => maxSmall.value || props.type === "kanban"
);

const desktopList = computed(() => !maxSmall.value && props.type === "list");

const clickTask = (ev) => {
  // prevent click event when clicking on the checkbox
  if (ev.target.getAttribute("role") !== "checkbox") {
    emit("click");
  }
};

// tooltip for description if the description is too long
// timeout to: limit the number of times the function is called, and to wait for the description to be rendered and ref to the element created
let tooltipTimeout = null;
const calculateDescriptionTooltip = () => {
  if (tooltipTimeout) clearTimeout(tooltipTimeout);
  const container = descriptionContainer?.value;
  if (!container) return;
  tooltipTimeout = setTimeout(() => {
    showDescriptionTooltip.value =
      container.scrollWidth > container.offsetWidth ||
      container.scrollHeight > container.clientHeight;
  }, 500);
};

onMounted(() => {
  setTimeout(() => {
    calculateDescriptionTooltip();
  }, 500);
});

watch(
  () => width.value,
  () => {
    calculateDescriptionTooltip();
  },
  { immediate: true }
);
</script>
